import { Controller } from "@hotwired/stimulus"

import { ExtensionClient } from "../extension"
import type { CursorPackPayload } from "../extension"
import { PromoPopupManager } from "../services/promo_popup_manager"
import { trackCursorPackEvent } from "../services/track_cursor_pack_event"

type State = "loading" | "add" | "added"

export default class extends Controller<HTMLElement> {
  static targets = ["loading", "add", "added"]

  declare readonly loadingTarget: HTMLElement
  declare readonly addTarget: HTMLElement
  declare readonly addedTarget: HTMLElement
  declare unsubscribe?: () => void

  connect() {
    this.unsubscribe = ExtensionClient.onConnectionChange(() => this.updateView())
    this.updateView()
  }

  disconnect() {
    this.unsubscribe?.()
  }

  // No loading state here: the server renders it as the default, and on
  // re-checks the current button stays put until the fresh answer arrives.
  async updateView() {
    const added = await ExtensionClient.isCursorPackAdded(this.#cursorPackPayload.cursorConfig.id)
    this.#show(added ? "added" : "add")
  }

  async add() {
    if (ExtensionClient.notSupported) {
      this.#popup("extension-unsupported")
      return
    }

    if (ExtensionClient.notConnected) {
      this.#popup("extension-install")
      return
    }

    this.#show("loading")
    if (await ExtensionClient.addCursorPack(this.#cursorPackPayload)) {
      this.#show("added")
      trackCursorPackEvent(this.element.dataset.cursorPackEventsUrl, "install")
      PromoPopupManager.recordActivity()
    } else {
      this.#show("add")
      this.#toast("Something went wrong adding this cursor pack. Please try again.", "error")
    }
  }

  #parsedCursorPackPayload?: CursorPackPayload
  get #cursorPackPayload(): CursorPackPayload {
    return (this.#parsedCursorPackPayload ??= JSON.parse(this.element.dataset.cursorPackPayload!))
  }

  #show(state: State) {
    this.loadingTarget.classList.toggle("hidden", state !== "loading")
    this.addTarget.classList.toggle("hidden", state !== "add")
    this.addedTarget.classList.toggle("hidden", state !== "added")
  }

  #toast(message: string, variant: "success" | "error" = "success") {
    this.dispatch("show", { prefix: "toast", target: window, detail: { message, variant } })
  }

  #popup(name: string) {
    this.dispatch("show", { prefix: "popup", target: window, detail: { name } })
  }
}
